import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";
import { COMPANY } from "@/data/content";

// Impressum — required under §5 TMG for any German commercial entity.
// Every field on this page has been provided by the client and is rendered
// verbatim.

const IMPRESSUM = {
  managingDirector: "Aelbehrt Schäfer",
  responsibleForContent: "Adam Seifert",
  court: "Amtsgericht Charlottenburg",
  hrb: "HRB 346545 B",
  vatId: "DE 423 234 564",
};

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-5 border-b border-white/10">
    <div className="md:col-span-4 text-xs uppercase tracking-widest2 text-bone-200/50">
      {label}
    </div>
    <div
      className="md:col-span-8 text-bone-100/90 leading-relaxed"
      data-testid={testid}
    >
      {children}
    </div>
  </div>
);

export default function Impressum() {
  return (
    <div className="relative">
      <section className="pt-24 md:pt-36 pb-16">
        <div className="container-x max-w-4xl">
          <Reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-bone-200/60 hover:text-clay-400 transition-colors mb-12"
              data-testid="impressum-back"
            >
              <ArrowLeft size={14} />
              Zurück zur Startseite
            </Link>
          </Reveal>

          <Reveal delay={40}>
            <p className="eyebrow mb-6">Legal · §5 TMG</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1">Impressum.</h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="body-lg mt-8 max-w-2xl">
              Angaben gemäß §5 TMG (Telemediengesetz) sowie §55 RStV.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-x max-w-4xl">
          <Reveal>
            <div className="card-surface p-8 md:p-12">
              <h2 className="display-h3 mb-8">Angaben zum Unternehmen</h2>

              <Row label="Firma / Company" testid="impressum-company">
                {COMPANY.legalName}
              </Row>

              <Row label="Anschrift / Address" testid="impressum-address">
                {COMPANY.address.line1}
                <br />
                {COMPANY.address.postal}
                <br />
                {COMPANY.address.country}
              </Row>

              <Row label="Vertretungsberechtigter Geschäftsführer" testid="impressum-managing-director">
                {IMPRESSUM.managingDirector}
              </Row>

              <Row label="Registergericht" testid="impressum-court">
                {IMPRESSUM.court}
              </Row>

              <Row label="Handelsregisternummer" testid="impressum-hrb">
                {IMPRESSUM.hrb}
              </Row>

              <Row label="Umsatzsteuer-ID" testid="impressum-vat">
                <span>{IMPRESSUM.vatId}</span>
                <div className="mt-1 text-xs text-bone-200/50">
                  gemäß §27a Umsatzsteuergesetz
                </div>
              </Row>

              <Row label="Verantwortlich für den Inhalt (V.i.S.d.P.)" testid="impressum-responsible">
                {IMPRESSUM.responsibleForContent}
                <br />
                <span className="text-sm text-bone-200/60">
                  gemäß §55 Abs. 2 RStV
                </span>
              </Row>

              <Row label="Kontakt" testid="impressum-contact">
                <div className="space-y-1">
                  <div>
                    Telefon:{" "}
                    <a
                      href={`tel:${COMPANY.phoneHref}`}
                      className="text-clay-400 hover:underline"
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                  <div>
                    E-Mail:{" "}
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-clay-400 hover:underline"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </div>
              </Row>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="card-surface p-8 md:p-12 mt-8">
              <h2 className="display-h3 mb-6">
                EU-Streitschlichtung
              </h2>
              <p className="text-bone-200/80 leading-relaxed mb-6">
                Die Europäische Kommission stellt eine Plattform zur
                Online-Streitbeilegung (OS) bereit:{" "}
                <a
                  href="https://ec.europa.eu/consumers/odr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-clay-400 hover:underline"
                >
                  https://ec.europa.eu/consumers/odr
                </a>
                . Unsere E-Mail-Adresse finden Sie oben im Impressum.
              </p>
              <p className="text-bone-200/80 leading-relaxed">
                Wir sind nicht bereit oder verpflichtet, an
                Streitbeilegungsverfahren vor einer
                Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="card-surface p-8 md:p-12 mt-8">
              <h2 className="display-h3 mb-6">Haftungsausschluss</h2>
              <div className="space-y-6 text-bone-200/80 leading-relaxed">
                <div>
                  <p className="text-bone-100 font-semibold mb-2">
                    Haftung für Inhalte
                  </p>
                  <p>
                    Als Diensteanbieter sind wir gemäß §7 Abs.1 TMG für eigene
                    Inhalte auf diesen Seiten nach den allgemeinen Gesetzen
                    verantwortlich. Nach §§8 bis 10 TMG sind wir als
                    Diensteanbieter jedoch nicht verpflichtet, übermittelte
                    oder gespeicherte fremde Informationen zu überwachen oder
                    nach Umständen zu forschen, die auf eine rechtswidrige
                    Tätigkeit hinweisen.
                  </p>
                </div>
                <div>
                  <p className="text-bone-100 font-semibold mb-2">
                    Haftung für Links
                  </p>
                  <p>
                    Unser Angebot enthält Links zu externen Websites Dritter,
                    auf deren Inhalte wir keinen Einfluss haben. Deshalb können
                    wir für diese fremden Inhalte auch keine Gewähr übernehmen.
                    Für die Inhalte der verlinkten Seiten ist stets der
                    jeweilige Anbieter oder Betreiber der Seiten
                    verantwortlich.
                  </p>
                </div>
                <div>
                  <p className="text-bone-100 font-semibold mb-2">
                    Urheberrecht
                  </p>
                  <p>
                    Die durch die Seitenbetreiber erstellten Inhalte und Werke
                    auf diesen Seiten unterliegen dem deutschen Urheberrecht.
                    Beiträge Dritter sind als solche gekennzeichnet.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <p className="text-xs text-bone-200/50 mt-10">
              Siehe auch unsere{" "}
              <Link to="/privacy" className="text-clay-400 hover:underline">
                Datenschutzerklärung
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
